import styled from 'styled-components';

import { Stack } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { TableToolbarDate, ToolbarDateSelector } from './TableToolbarDate';
import {
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid';

const Container = styled.div`
  & .MuiDataGrid-toolbarContainer {
    padding: 8px 12px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid ${({ theme }) => theme.line.light};
    background-color: ${({ theme }) => theme.background.standard};
    min-height: 52px;
  }

  & .MuiButton-root {
    padding: 4px 8px;
    min-width: unset;
  }
`;

const Search = styled.div`
  & .MuiInputBase-root {
    background-color: ${({ theme }) => theme.background.standard};
    min-width: 20rem;
    height: 36px;
  }
`;

interface Props {
  exportPrefix: string;
  dateRange?: ToolbarDateSelector | undefined;
  toolbarSearchOnly?: boolean;
  toolbarSearchMargin?: number;
}

export const TableToolbar: React.FC<Props> = ({ exportPrefix, dateRange, toolbarSearchOnly, toolbarSearchMargin }) => {
  const today = new Date().toISOString();
  const location = useLocation();

  // Get the path segments from the URL
  const pathSegments = location.pathname.split('/').filter(Boolean);

  // Extract a meaningful resource name from the URL path
  const getResourceName = () => {
    if (pathSegments.length === 0) return 'table';

    // Use the last path segment that doesn't look like an ID
    // IDs typically contain special characters like hyphens, underscores, or are very long
    for (let i = pathSegments.length - 1; i >= 0; i--) {
      const segment = pathSegments[i];

      // Skip segments that look like IDs (contain hyphens, underscores, or are very long)
      if (!segment.includes('-') && !segment.includes('_') && segment.length < 20) {
        return segment;
      }
    }

    // If all segments look like IDs, use the last segment
    return pathSegments[pathSegments.length - 1];
  };

  const resourceName = getResourceName();
  const prefix = exportPrefix && exportPrefix !== 'table' ? exportPrefix : resourceName;

  if (toolbarSearchOnly) {
    return (
      <Stack sx={{ margin: `${toolbarSearchMargin}px`, marginBottom: 0 }}>
        <GridToolbarQuickFilter fullWidth variant='outlined' size='small' placeholder='Search...' />
      </Stack>
    );
  }

  return (
    <Container>
      <GridToolbarContainer>
        <Stack direction='row' spacing={0.5}>
          <GridToolbarColumnsButton sx={{ fontWeight: '500 !important' }} />
          <GridToolbarFilterButton />
          <GridToolbarDensitySelector />
          <GridToolbarExport csvOptions={{ fileName: `${prefix}-${today}` }} />
        </Stack>
        <Stack direction='row' spacing={1.5} alignItems='flex-start'>
          <Search>
            <GridToolbarQuickFilter variant='outlined' size='small' placeholder='Search...' />
          </Search>
          {dateRange && (
            <TableToolbarDate
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              onStartDateChange={dateRange.onStartDateChange}
              onEndDateChange={dateRange.onEndDateChange}
            />
          )}
        </Stack>
      </GridToolbarContainer>
    </Container>
  );
};
